import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import SEO from '../components/SEO';
import Icon from '../components/Icons';
import '../pages/Contact/styles.css';

const Contact = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    company: '',
    reason: ''
  });
  const particlesRef = useRef(null);
  const sectionsRef = useRef([]);

  // Create ambient particles
  useEffect(() => {
    const createParticle = () => {
      if (!particlesRef.current) return;
      const particle = document.createElement('div');
      particle.className = 'contact-particle';
      const size = Math.random() * 2 + 1;
      particle.style.width = `${size}px`;
      particle.style.height = `${size}px`;
      particle.style.left = `${Math.random() * 100}%`;
      particle.style.top = `${Math.random() * 100}%`;
      particle.style.animationDuration = `${15 + Math.random() * 20}s`;
      particle.style.animationDelay = `${Math.random() * 5}s`;
      particlesRef.current.appendChild(particle);

      setTimeout(() => {
        if (particle.parentNode) {
          particle.parentNode.removeChild(particle);
        }
      }, 35000);
    };

    // Create initial particles
    for (let i = 0; i < 20; i++) {
      setTimeout(() => createParticle(), i * 500);
    }

    // Continuously add particles
    const interval = setInterval(() => {
      if (particlesRef.current && particlesRef.current.children.length < 25) {
        createParticle();
      }
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  // Scroll reveal animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
        }
      });
    }, observerOptions);

    sectionsRef.current.forEach(section => {
      if (section) observer.observe(section);
    });

    return () => {
      sectionsRef.current.forEach(section => {
        if (section) observer.unobserve(section);
      });
    };
  }, []);

  // Pre-fill form from query parameters (from alignment form redirect)
  useEffect(() => {
    const name = searchParams.get('name') || '';
    const email = searchParams.get('email') || '';
    const message = searchParams.get('message') || '';
    const company = searchParams.get('company') || '';
    const reason = searchParams.get('reason') || '';

    if (name || email || message || company || reason) {
      setFormData({ name, email, message, company, reason });
      // Pre-fill react-hook-form
      reset({ name, email, message, company, reason });
    }
  }, [searchParams, reset]);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.eternavaults.com';

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const source = searchParams.get('source') || 'contact-page';

      const response = await fetch(`${API_BASE_URL}/api/public/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          message: data.message,
          company: data.company || '',
          reason: data.reason || '',
          source
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send message');
      }

      const result = await response.json();
      setSubmitStatus('success');

      // Redirect to thank-you page after short delay
      setTimeout(() => {
        navigate(`/thank-you?name=${encodeURIComponent(data.name)}&source=${encodeURIComponent(source)}`);
      }, 1500);
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact EternaVaults | Private Consultation & Discreet Enquiries"
        description="Begin a private conversation with EternaVaults. Our consultations are encrypted, confidential, and by invitation. Legacy secured for discerning families and trusted advisors."
        keywords="EternaVaults contact, private digital vault, legacy management, encrypted consultation, family office technology, estate planning infrastructure"
      />

      <div className="contact-page">
        {/* Ambient Particles */}
        <div className="particles-background" ref={particlesRef}></div>

        {/* Vertical Gold Spine */}
        <div className="gold-spine"></div>

        {/* HERO */}
        <section className="contact-hero">
          <div className="container">
            <h1>Concierge Access</h1>
            <p className="hero-subtitle">
              Private consultations available worldwide - conducted with absolute discretion.<br />
              <em>Your legacy is never just data. It's a story we're entrusted to protect.</em>
            </p>
          </div>
        </section>

        {/* INFO STRIP */}
        <section className="contact-highlights" ref={el => sectionsRef.current[0] = el}>
          <div className="container">
            <div className="highlight-grid">
              <div className="highlight-item">
                <Icon name="lock" size={24} />
                <h4>End-to-End Encryption</h4>
                <p>Every message begins encrypted - and remains unreadable to third parties.</p>
              </div>
              <div className="highlight-item">
                <Icon name="shield" size={24} />
                <h4>24/7 Secure Access</h4>
                <p>Our systems are continuously monitored for your peace of mind.</p>
              </div>
              <div className="highlight-item">
                <Icon name="key" size={24} />
                <h4>Elite Client Base</h4>
                <p>Trusted by family offices, advisors, and legacy custodians worldwide.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CONTACT INFO */}
        <section className="direct-contact" ref={el => sectionsRef.current[1] = el}>
          <div className="container">
            <h3>Direct Contact</h3>
            <p>
              <strong>Email:</strong> <a href="mailto:concierge@eternavaults.com">concierge@eternavaults.com</a>
            </p>
            <p>
              <small>All enquiries are encrypted and personally reviewed by our founder's office.</small>
            </p>
          </div>
        </section>

        {/* FORM */}
        <section className="contact-form-section" ref={el => sectionsRef.current[2] = el}>
          <div className="container">
            <h2>Begin a Private Conversation</h2>
            <p className="form-intro">
              All consultations are confidential. Messages are encrypted at transmission and never shared.
              If you're enquiring about partnerships, investment, or private editions, please indicate below.
            </p>
            <p className="private-editions-note" style={{
              marginTop: '1rem',
              padding: '1rem',
              background: 'rgba(226, 200, 156, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(226, 200, 156, 0.2)',
              color: '#e2c89c',
              fontSize: '0.95rem',
              fontStyle: 'italic'
            }}>
              <strong>Private Edition Enquiries:</strong> Get in touch for more info and request for private-editions-pack under NDA.
            </p>

            {submitStatus === 'success' && (
              <div className="form-success">
                <Icon name="check" size={24} />
                <p>Your message has been sent successfully. Redirecting...</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="form-error">
                <Icon name="alert" size={24} />
                <p>There was an error sending your message. Please try again or contact us directly.</p>
              </div>
            )}

            <form className="secure-contact-form" onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group">
                <label>Name</label>
                <input
                  {...register("name", { required: "Name is required" })}
                  type="text"
                />
                {errors.name && <span className="error-message">{errors.name.message}</span>}
              </div>
              <div className="form-group">
                <label>Secure Email</label>
                <input
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address"
                    }
                  })}
                  type="email"
                />
                {errors.email && <span className="error-message">{errors.email.message}</span>}
              </div>
              <div className="form-group">
                <label>Reason for Contact</label>
                <select
                  {...register("reason", { required: "Please select a reason for contact" })}
                  className="contact-reason-dropdown"
                >
                  <option value="">Select a reason...</option>
                  <option value="private-edition-inquiry">Private Edition Inquiry</option>
                  <option value="private-editions-pack-nda">Request Private Editions Pack (under NDA)</option>
                  <option value="imperium-consultation">Imperium Plan Consultation</option>
                  <option value="partnership-opportunity">Partnership Opportunity</option>
                  <option value="investment-inquiry">Investment Inquiry</option>
                  <option value="white-label-inquiry">Custodied Infrastructure Partnership</option>
                  <option value="general-inquiry">General Inquiry</option>
                  <option value="technical-support">Technical Support</option>
                  <option value="billing-question">Billing Question</option>
                  <option value="other">Other</option>
                </select>
                {errors.reason && <span className="error-message">{errors.reason.message}</span>}
              </div>
              {searchParams.get('source') === 'white-label-inquiry' && (
                <div className="form-group">
                  <label>Company</label>
                  <input
                    {...register("company")}
                    type="text"
                  />
                </div>
              )}
              <div className="form-group">
                <label>Message</label>
                <textarea
                  {...register("message", { required: "Message is required" })}
                  rows="5"
                  placeholder="Tell us briefly how we can assist — partnership, family access, or private consultation..."
                ></textarea>
                {errors.message && <span className="error-message">{errors.message.message}</span>}
              </div>
              <button
                type="submit"
                className="cta-button primary"
                disabled={isSubmitting}
              >
                <Icon name="mail" size={18} />
                {isSubmitting ? 'Sending...' : 'Submit Confidential Request'}
              </button>
            </form>

            <p className="discretion-note">
              <em>Every enquiry is treated as privileged correspondence — no automation, no public logs.</em>
            </p>
          </div>
        </section>

        {/* CLOSING MESSAGE */}
        <section className="contact-cta" ref={el => sectionsRef.current[3] = el}>
          <div className="container">
            <h2>Legacy, Secured - Conversations That Endure</h2>
            <p>
              Whether you represent a private family office, boutique advisory, or institution - each discussion begins with trust, not transaction.
              <br /><br />
              <strong>EternaVaults Private Edition</strong> remains available only to a select number of partners and patrons each year.
              We vet every request to ensure values align with permanence, privacy, and purpose.
            </p>
            <div className="cta-buttons">
              <a href="mailto:concierge@eternavaults.com" className="cta-button secondary">
                <Icon name="calendar" size={18} /> Request Private Consultation
              </a>
            </div>
          </div>
        </section>

        {/* FOOTNOTE */}
        <section className="contact-footer">
          <div className="container">
            <p>
              <em>
                "True legacy is measured not by what we possess — but by what endures beyond us."
              </em>
            </p>
            <p className="footnote-small">
              Powered by zero-knowledge technology. Operated in strict confidentiality. © {new Date().getFullYear()} EternaVaults.
            </p>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contact;
